import React, { useState, useEffect } from 'react';
import { 
  // Alert icons (matching dashboard)
  AlertTriangle,
  AlertCircle,
  XCircle,
  
  // Navigation & UI
  Menu,
  X,
  ChevronDown,
  ArrowRight,
  Check,
  
  // Feature icons (matching dashboard tabs & features)
  Clock,
  DollarSign,
  Users,
  UserCheck,
  Upload,
  Activity,
  History,
  
  // Additional icons
  Star,
  Zap,
  FolderSync,
} from 'lucide-react';

// ============================================
// CONFIGURATION
// ============================================
const CONFIG = {
  appUrl: '/app',
  demoUrl: '#contact',
  logoText: 'LTSS Analytics',
  stats: {
    reportsAnalyzed: '500+',
    alertTypes: '8',
    revenueRecovered: '$47,000+'
  },
  headlines: {
    primary: "Find the $2,400 hiding in your EVV reports",
    sub: "Upload your Sandata export. See missed clock-ins, penalty risks, and unbilled services in 30 seconds.",
  }
};

// ============================================
// COLOR SYSTEM (matches dashboard)
// ============================================
// Primary: blue-500 (#3b82f6) - CTAs, links, accents
// Background Dark: slate-900 (#0f172a) - Hero, footer
// Background Light: slate-50 (#f8fafc) - Alternating sections
// Critical: red-500 (#ef4444) - Alerts, urgent
// Warning: yellow-500 (#eab308) - Caution states  
// Success: emerald-500 (#10b981) - Positive metrics
// Text Dark: gray-900 (#111827) - Headlines
// Text Light: slate-300 (#cbd5e1) - Dark bg text

// ============================================
// NAVBAR - Adapts to dark hero
// ============================================
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-sm shadow-sm' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className={`font-bold text-xl transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              {CONFIG.logoText}
            </span>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-slate-300 hover:text-white'}`}>Features</a>
            <a href="#pricing" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-slate-300 hover:text-white'}`}>Pricing</a>
            <a href="#faq" className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-slate-300 hover:text-white'}`}>FAQ</a>
            <a href={CONFIG.appUrl} className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-slate-300 hover:text-white'}`}>Login</a>
            <a 
              href={CONFIG.appUrl} 
              className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-400 transition-colors"
            >
              Try Free
            </a>
          </div>

          <button 
            className={`md:hidden p-2 ${scrolled ? 'text-gray-900' : 'text-white'}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a href="#features" className="block px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">Features</a>
              <a href="#pricing" className="block px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">Pricing</a>
              <a href="#faq" className="block px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">FAQ</a>
              <a href={CONFIG.appUrl} className="block px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">Login</a>
              <a 
                href={CONFIG.appUrl} 
                className="block px-3 py-2 bg-blue-500 text-white rounded-lg text-center font-medium mt-2"
              >
                Try Free
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// ============================================
// HERO SECTION - Conversion-optimized with slideshow
// ============================================
const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    { src: '/images/dashboard-alerts.png', alt: 'LTSS Analytics Dashboard - Alerts View' },
    { src: '/images/dashboard-staff.png', alt: 'LTSS Analytics Dashboard - Staff View' },
    { src: '/images/dashboard-financial.png', alt: 'LTSS Analytics Dashboard - Financial View' },
  ];

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change every 5 seconds
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="relative pt-20 pb-16 md:pt-28 md:pb-20 overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] -z-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Centered hero content */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          {/* Trust badge */}
          <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400"></span>
            </span>
            <span>Built for Maryland Home Healthcare Agencies</span>
          </div>

          {/* Main headline - specific, benefit-driven */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Find the <span className="text-emerald-400">$2,400</span> hiding in your EVV reports
          </h1>

          {/* Subheadline - clear, specific value prop */}
          <p className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-3xl mx-auto mb-8">
            {CONFIG.headlines.sub}
          </p>

          {/* Single primary CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a 
              href={CONFIG.appUrl}
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-400 transition-all hover:shadow-lg hover:shadow-blue-500/25 group text-lg"
            >
              Analyze Your First Report Free
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Trust signals - horizontal */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Works with Sandata CSV/Excel</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Results in 30 seconds</span>
            </div>
          </div>
        </div>

        {/* Dashboard preview - the money shot */}
        <div className="relative max-w-5xl mx-auto">
          {/* Glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-purple-500/10 to-blue-500/20 rounded-3xl blur-3xl -z-10" />
          
          {/* Browser frame */}
          <div className="relative bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 bg-slate-800 border-b border-slate-700">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-400 text-center max-w-xs mx-auto">
                  ltssanalytics.com/app
                </div>
              </div>
            </div>
            
            {/* Screenshot Slideshow */}
            <div className="aspect-[16/9] bg-slate-900 relative overflow-hidden">
              {slides.map((slide, index) => (
                <img 
                  key={index}
                  src={slide.src} 
                  alt={slide.alt}
                  className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              
              {/* Slide indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-blue-500 w-6' 
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Floating stat cards */}
          {/* Floating alert card - matches dashboard red alert style */}
          <div className="absolute -left-4 lg:-left-12 bottom-8 lg:bottom-16 bg-slate-800 rounded-xl shadow-xl border border-red-500/30 p-4 max-w-[200px] animate-float hidden md:block">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">3 Critical</p>
                <p className="text-sm text-slate-400">Missing clock-ins</p>
              </div>
            </div>
          </div>

          {/* Floating revenue card - matches dashboard emerald success style */}
          <div className="absolute -right-4 lg:-right-12 top-16 lg:top-24 bg-slate-800 rounded-xl shadow-xl border border-emerald-500/30 p-4 max-w-[180px] animate-float-delayed hidden md:block">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">$2,847</p>
                <p className="text-sm text-slate-400">At risk this week</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SOCIAL PROOF BAR - Logo strip
// ============================================
const LogoBar = () => {
  return (
    <section className="py-12 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-sm text-slate-500 mb-6 uppercase tracking-wider font-medium">
          Trusted by Maryland home healthcare agencies
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-60">
          {/* Placeholder for agency logos - replace with real logos */}
          <div className="text-slate-400 font-semibold text-lg">CFC Agency</div>
          <div className="text-slate-400 font-semibold text-lg">HomeFirst Care</div>
          <div className="text-slate-400 font-semibold text-lg">Maryland LTSS</div>
          <div className="text-slate-400 font-semibold text-lg">CareBridge</div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// PROBLEM SECTION - Agitate the pain
// ============================================
const Problem = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Every week, you're losing money you can't see
          </h2>
          <p className="text-xl text-gray-600">
            These issues hide in your Sandata exports. By the time you find them manually, it's too late.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {/* Missed Clock-Ins */}
          <div className="relative bg-slate-900 rounded-2xl p-8 border border-red-500/20 hover:shadow-lg transition-all duration-300">
            <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mb-5">
              <Clock className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Missed Clock-Ins</h3>
            <div className="mb-3">
              <span className="text-2xl font-bold text-red-500">$50-200</span>
              <span className="text-sm text-slate-400 ml-2">lost per incident</span>
            </div>
            <p className="text-slate-300 leading-relaxed">
              A caregiver forgets to clock in. By the time you notice in the spreadsheet, the correction window has closed.
            </p>
          </div>

          {/* Penalty Points */}
          <div className="relative bg-slate-900 rounded-2xl p-8 border border-yellow-500/20 hover:shadow-lg transition-all duration-300">
            <div className="w-12 h-12 bg-yellow-500/10 rounded-xl flex items-center justify-center mb-5">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Penalty Points</h3>
            <div className="mb-3">
              <span className="text-2xl font-bold text-yellow-500">4 points</span>
              <span className="text-sm text-slate-400 ml-2">triggers MDH audit</span>
            </div>
            <p className="text-slate-300 leading-relaxed">
              Staff accumulating points you can't see. Most agencies find out when the audit letter arrives.
            </p>
          </div>

          {/* Over-Authorization */}
          <div className="relative bg-slate-900 rounded-2xl p-8 border border-red-500/20 hover:shadow-lg transition-all duration-300">
            <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mb-5">
              <XCircle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Over-Authorization</h3>
            <div className="mb-3">
              <span className="text-2xl font-bold text-red-500">100%</span>
              <span className="text-sm text-slate-400 ml-2">claim rejection</span>
            </div>
            <p className="text-slate-300 leading-relaxed">
              Services outside authorization windows get rejected. You did the work. You don't get paid.
            </p>
          </div>
        </div>

        {/* Testimonial - social proof */}
        <div className="max-w-3xl mx-auto">
          <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 md:p-10">
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <blockquote className="text-xl text-white leading-relaxed mb-6">
              "I used to spend 3 hours every pay period hunting through spreadsheets. 
              Now I upload the file and see everything in 30 seconds. Found $1,800 in 
              missed revenue the first week."
            </blockquote>
            <footer className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                JM
              </div>
              <div>
                <p className="font-semibold text-white">Office Manager</p>
                <p className="text-slate-400">Baltimore County Home Healthcare</p>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SOLUTION SECTION - How it works
// ============================================
const Solution = () => {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-blue-500 font-semibold mb-3 uppercase tracking-wide text-sm">How it works</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            From export to insights in 30 seconds
          </h2>
          <p className="text-xl text-gray-600">
            No training. No setup. No IT department needed.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Connection line */}
          <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200" />
          
          {/* Step 1 */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-blue-500 text-white text-lg font-bold rounded-full flex items-center justify-center shadow-lg">
                1
              </div>
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-5 mt-4 mx-auto">
                <Upload className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Export from Sandata</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Download your Services Rendered report as CSV or Excel. Takes 30 seconds.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-blue-500 text-white text-lg font-bold rounded-full flex items-center justify-center shadow-lg">
                2
              </div>
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-5 mt-4 mx-auto">
                <Zap className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Drop it in LTSS Analytics</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Drag and drop. We parse thousands of rows instantly. No setup required.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-blue-500 text-white text-lg font-bold rounded-full flex items-center justify-center shadow-lg">
                3
              </div>
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-5 mt-4 mx-auto">
                <Activity className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">See what you've been missing</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Critical alerts, penalty tracking, revenue at risk—all in one dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a 
            href={CONFIG.appUrl}
            className="inline-flex items-center justify-center px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all hover:shadow-lg group"
          >
            Try Your First Report Free
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FEATURES SECTION - What you get
// ============================================
const Features = () => {
  return (
    <section id="features" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-blue-500 font-semibold mb-3 uppercase tracking-wide text-sm">Features</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Everything you need to protect your revenue
          </h2>
          <p className="text-xl text-gray-600">
            Built by people who understand LTSS billing. No fluff, just what matters.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* 8 Compliance Alerts */}
          <div className="group p-6 rounded-2xl bg-slate-50 hover:bg-slate-900 border border-slate-200 hover:border-slate-700 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
              <AlertCircle className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors">8 Compliance Alerts</h3>
            <p className="text-gray-600 group-hover:text-slate-300 leading-relaxed transition-colors">
              Missing clock-ins, unauthorized services, overlapping shifts, penalty points—all flagged automatically.
            </p>
          </div>

          {/* Staff Performance */}
          <div className="group p-6 rounded-2xl bg-slate-50 hover:bg-slate-900 border border-slate-200 hover:border-slate-700 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors">Staff Performance</h3>
            <p className="text-gray-600 group-hover:text-slate-300 leading-relaxed transition-colors">
              Hours worked, billable vs actual, clean clock-in rates. Know who needs coaching.
            </p>
          </div>

          {/* Client Utilization */}
          <div className="group p-6 rounded-2xl bg-slate-50 hover:bg-slate-900 border border-slate-200 hover:border-slate-700 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
              <UserCheck className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors">Client Utilization</h3>
            <p className="text-gray-600 group-hover:text-slate-300 leading-relaxed transition-colors">
              Are you using all authorized hours? See utilization rates and missed revenue by client.
            </p>
          </div>

          {/* Revenue Insights */}
          <div className="group p-6 rounded-2xl bg-slate-50 hover:bg-slate-900 border border-slate-200 hover:border-slate-700 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
              <DollarSign className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors">Revenue Insights</h3>
            <p className="text-gray-600 group-hover:text-slate-300 leading-relaxed transition-colors">
              Revenue by procedure code, leakage detection, payroll estimates.
            </p>
          </div>

          {/* Trend Tracking */}
          <div className="group p-6 rounded-2xl bg-slate-50 hover:bg-slate-900 border border-slate-200 hover:border-slate-700 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
              <History className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors">Trend Tracking</h3>
            <p className="text-gray-600 group-hover:text-slate-300 leading-relaxed transition-colors">
              Compare pay periods. See if problems are getting better or worse over time.
            </p>
          </div>

          {/* Google Drive Sync */}
          <div className="group p-6 rounded-2xl bg-slate-50 hover:bg-slate-900 border border-slate-200 hover:border-slate-700 transition-all duration-300">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
              <FolderSync className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-white mb-2 transition-colors">Google Drive Sync</h3>
            <p className="text-gray-600 group-hover:text-slate-300 leading-relaxed transition-colors">
              Connect your Drive. Import reports without downloading files manually.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// PRICING SECTION
// ============================================
const Pricing = () => {
  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      description: 'Perfect for trying out LTSS Analytics',
      features: [
        '3 report uploads/month',
        'All 8 compliance alerts',
        'Staff & Client views',
        'Basic financial summary',
        'Browser-based storage',
      ],
      cta: 'Start Free',
      ctaLink: CONFIG.appUrl,
      highlighted: false,
    },
    {
      name: 'Pro',
      price: '$79',
      period: '/month',
      description: 'For agencies serious about revenue protection',
      features: [
        'Unlimited report uploads',
        'PDF audit reports for MDH',
        'Revenue leakage detection',
        'Missed revenue analysis',
        'Payroll estimation',
        'Webhook integrations',
        'Custom alert thresholds',
        'Email support',
      ],
      cta: 'Start Pro Trial',
      ctaLink: CONFIG.appUrl,
      highlighted: true,
      badge: 'Most Popular',
    },
    {
      name: 'Enterprise',
      price: '$199',
      period: '/month',
      description: 'For multi-location agencies',
      features: [
        'Everything in Pro',
        'Multi-location management',
        'Team accounts (up to 10)',
        'Role-based access control',
        'API access (1,000 req/hr)',
        'Priority support',
        'Onboarding assistance',
      ],
      cta: 'Contact Us',
      ctaLink: CONFIG.demoUrl,
      highlighted: false,
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Pricing That Makes Sense
          </h2>
          <p className="text-xl text-gray-600">
            Start free. Upgrade when you're ready.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`relative rounded-2xl p-8 ${
                plan.highlighted 
                  ? 'bg-slate-900 text-white shadow-xl shadow-blue-500/20 scale-105 border border-blue-500/30' 
                  : 'bg-white border border-slate-200'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-sm font-bold px-4 py-1 rounded-full">
                  {plan.badge}
                </div>
              )}

              <h3 className={`text-xl font-semibold mb-2 ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                {plan.name}
              </h3>
              
              <div className="mb-4">
                <span className={`text-4xl font-bold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                  {plan.price}
                </span>
                <span className={plan.highlighted ? 'text-slate-400' : 'text-gray-500'}>
                  {plan.period}
                </span>
              </div>

              <p className={`mb-6 ${plan.highlighted ? 'text-slate-300' : 'text-gray-600'}`}>
                {plan.description}
              </p>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 flex-shrink-0 ${plan.highlighted ? 'text-emerald-400' : 'text-emerald-500'}`} />
                    <span className={plan.highlighted ? 'text-slate-300' : 'text-gray-600'}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href={plan.ctaLink}
                className={`block text-center py-3 px-6 rounded-xl font-semibold transition-all ${
                  plan.highlighted
                    ? 'bg-blue-500 text-white hover:bg-blue-400'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-500 mt-8">
          All plans include a 14-day free trial. No credit card required to start.
        </p>
      </div>
    </section>
  );
};

// ============================================
// FAQ SECTION
// ============================================
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'Does this replace my EVV system?',
      answer: 'No. LTSS Analytics works alongside your existing EVV workflow. You keep using Sandata for clock-ins. We analyze the data after to find problems and revenue gaps.',
    },
    {
      question: 'Is my data secure?',
      answer: 'Your data is processed in your browser and stored locally using IndexedDB. We don\'t store PHI on our servers. Enterprise plans offer additional security options and team management.',
    },
    {
      question: 'What states do you support?',
      answer: 'We\'re currently optimized for Maryland LTSS/Sandata exports. We\'re expanding to other states in 2025. Contact us if you\'re interested in early access for your state.',
    },
    {
      question: 'Can my whole team use it?',
      answer: 'Free and Pro plans are single-user. Enterprise supports up to 10 team members with role-based access control (Owner, Admin, Manager, Viewer roles).',
    },
    {
      question: 'What file formats do you accept?',
      answer: 'We accept CSV and Excel (.xls, .xlsx) exports from Sandata/LTSS. You can also import directly from Google Drive, including Google Sheets.',
    },
    {
      question: 'What if I need help?',
      answer: 'Free tier includes documentation and community support. Pro includes email support. Enterprise includes priority support and onboarding assistance.',
    },
  ];

  return (
    <section id="faq" className="py-20 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about LTSS Analytics.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <button
                className="w-full px-6 py-4 text-left flex items-center justify-between gap-4"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="font-semibold text-gray-900">{faq.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FINAL CTA SECTION
// ============================================
const FinalCTA = () => {
  return (
    <section id="contact" className="py-20 bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
          Stop Losing Money to Invisible Compliance Gaps
        </h2>
        <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
          Your first 3 reports are free. See what you've been missing.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href={CONFIG.appUrl}
            className="inline-flex items-center justify-center px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-400 transition-all group"
          >
            Start Free Analysis
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <a 
            href={CONFIG.demoUrl}
            className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-slate-600 hover:border-slate-500 hover:bg-slate-800 transition-all"
          >
            Schedule a Demo
          </a>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOOTER
// ============================================
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-400 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white">{CONFIG.logoText}</span>
            </div>
            <p className="text-gray-400 max-w-md">
              Revenue protection and compliance analytics for Maryland home healthcare agencies. 
              Turn your LTSS reports into actionable insights.
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Product</h4>
            <ul className="space-y-2">
              <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              <li><a href={CONFIG.appUrl} className="hover:text-white transition-colors">Login</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm">
            © {new Date().getFullYear()} LTSS Analytics. All rights reserved.
          </p>
          <p className="text-sm">
            Built for Maryland Home Healthcare Agencies
          </p>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// MAIN LANDING PAGE COMPONENT
// ============================================
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <LogoBar />
      <Problem />
      <Solution />
      <Features />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
